/**
 * LinkedContextPeek — espiada de contexto vinculado DENTRO do popover
 * (Mensageria Condensada · superfície 3 · PR F1b).
 *
 * Disparado pelo chip âncora. JAMAIS navega para fora (P13) — é uma view do
 * popover, com "voltar" ao thread/lista. Tabs = portas para o que JÁ existe na
 * conversa ancorada: Resumo Vivo (6 blocos, somente leitura, "Gerado por IA"),
 * Mídias (apenas recebidas — sem upload), Notas (corpo + autor + visibilidade).
 *
 * Permissão na ponta que recebe: a porta checa capability + escopo de leitura no
 * backend. 403 → estado honesto "sem permissão" (cadeado + motivo + etiqueta de
 * sensibilidade), nunca conteúdo. Decide `denied` internamente (do hook), não por
 * prop — o popover só passa o `conversationId`.
 */

import { ChevronLeft, FileText, Image as ImageIcon, Lock, Search, Sparkles } from 'lucide-react';
import { useState } from 'react';

import { useContextPeek } from '../hooks/use-context-peek';

import type {
  ContextPeekLivingSummary,
  ContextPeekMediaItem,
  ContextPeekNoteItem,
} from '../../types';

type PeekTab = 'resumo' | 'midias' | 'notas';

interface LinkedContextPeekProps {
  /** Conversa ancorada a espiar; null = nenhuma âncora (degrada honesto). */
  conversationId: string | null;
  /** Thread interno de origem (auditoria server-side). */
  viaThreadId?: string | null;
  onBack: () => void;
}

/** Rótulo honesto do título (o contrato do peek não traz nome rico do cliente). */
function conversationTitle(conversationId: string | null): string {
  if (conversationId === null || conversationId.length === 0) return 'Conversa [sem nome]';
  return `Conversa ${conversationId}`;
}

/** Mapeia sensitivityLevel (1–4, ADR-024) → etiqueta + classe de escala do DS. */
function sensitivityTag(level: number | null): { label: string; cls: string } | null {
  switch (level) {
    case 2:
      return { label: 'N2 — Restrita', cls: 'mc-sens--n2' };
    case 3:
      return { label: 'N3 — Sensível', cls: 'mc-sens--n3' };
    case 4:
      return { label: 'N4 — Crítica', cls: 'mc-sens--n4' };
    default:
      // N1 (Pública) ou desconhecido → sem etiqueta de sensibilidade.
      return null;
  }
}

export function LinkedContextPeek({
  conversationId,
  viaThreadId = null,
  onBack,
}: LinkedContextPeekProps): JSX.Element {
  const [tab, setTab] = useState<PeekTab>('resumo');
  const { data, denied, deniedDetail, isLoading, isError } = useContextPeek(conversationId, {
    viaThreadId,
  });

  const title = conversationTitle(conversationId);
  const okTag = sensitivityTag(data?.sensitivityLevel ?? null);
  const deniedTag = sensitivityTag(deniedDetail?.sensitivityLevel ?? null);

  return (
    <div className="mc-view mc-view--flex">
      <div className="mc-subheader">
        <button
          type="button"
          className="mc-icon-btn"
          aria-label="Voltar à conversa interna"
          onClick={onBack}
        >
          <ChevronLeft size={15} strokeWidth={1.7} aria-hidden />
        </button>
        <span className="mc-subheader-title">
          <b>{title}</b>
          <span className="mc-mono-meta">
            {denied ? 'contexto vinculado' : 'espiada · você não saiu da mensageria'}
          </span>
        </span>
        {!denied && !isLoading && !isError && (
          <span className="mc-ai-chip">
            <Search size={10} strokeWidth={1.6} aria-hidden />
            peek
          </span>
        )}
      </div>

      {denied ? (
        <DeniedState reason={deniedDetail?.reason ?? null} tag={deniedTag} />
      ) : isLoading ? (
        <div className="mc-section" role="status">
          <span className="mc-mono-meta">Abrindo contexto…</span>
        </div>
      ) : isError || data === undefined ? (
        <div className="mc-section">
          <span className="mc-mono-meta">Contexto indisponível. Tente novamente.</span>
        </div>
      ) : (
        <>
          <div className="mc-tabs" role="tablist" aria-label="Contexto da conversa vinculada">
            {(
              [
                ['resumo', 'Resumo Vivo'],
                ['midias', 'Mídias'],
                ['notas', 'Notas'],
              ] as [PeekTab, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`mc-tab${tab === key ? ' is-active' : ''}`}
                onClick={() => {
                  setTab(key);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="mc-peek-body mc-scroll" role="tabpanel">
            {tab === 'resumo' && <PeekResumo summary={data.livingSummary} tag={okTag} />}
            {tab === 'midias' && <PeekMidias media={data.media} />}
            {tab === 'notas' && <PeekNotas notes={data.notes} />}
          </div>
        </>
      )}
    </div>
  );
}

function DeniedState({
  reason,
  tag,
}: {
  reason: 'not-assigned' | 'role-not-permitted' | null;
  tag: { label: string; cls: string } | null;
}): JSX.Element {
  const detail =
    reason === 'role-not-permitted'
      ? 'Seu papel não permite ler o conteúdo desta conversa.'
      : 'Ela está fora do seu escopo de supervisão.';
  return (
    <div className="mc-denied" role="status">
      <span className="mc-denied-glyph" aria-hidden>
        <Lock size={20} strokeWidth={1.5} />
      </span>
      <div className="mc-denied-text">
        <b>Você não pode ver o conteúdo desta conversa</b>
        <span>{detail} O Resumo Vivo, mídias e notas ficam ocultos para você.</span>
      </div>
      {tag !== null && (
        <span className={`mc-sens ${tag.cls}`}>
          <span className="mc-sens-dot" aria-hidden />
          {tag.label}
        </span>
      )}
    </div>
  );
}

/** Os 6 blocos canônicos do Resumo Vivo; arrays viram listas (R15). */
function PeekResumo({
  summary,
  tag,
}: {
  summary: ContextPeekLivingSummary | null;
  tag: { label: string; cls: string } | null;
}): JSX.Element {
  return (
    <div className="mc-summary">
      <div className="mc-ai-eyebrow">
        <Sparkles size={12} strokeWidth={1.9} aria-hidden />
        Gerado por IA · somente leitura
      </div>
      {tag !== null && (
        <div className="mc-summary-block">
          <span className="mc-eyebrow">Sensibilidade</span>
          <span className={`mc-sens ${tag.cls}`}>
            <span className="mc-sens-dot" aria-hidden />
            {tag.label}
          </span>
        </div>
      )}
      {summary === null ? (
        <span className="mc-summary-value">Resumo Vivo ainda não gerado para esta conversa.</span>
      ) : (
        <>
          <TextBlock label="Contexto original" value={summary.contextOriginal} />
          <TextBlock label="Evolução da conversa" value={summary.evolucaoConversa} />
          <TextBlock label="Necessidade atual" value={summary.necessidadeAtual} />
          <ListBlock label="Pendências e acordos" items={summary.pendenciasAcordos} />
          <TextBlock label="Confiança e sentimento" value={summary.confiancaSentimento} />
          <ListBlock label="Riscos e observações" items={summary.riscosObservacoes} />
        </>
      )}
    </div>
  );
}

function TextBlock({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <div className="mc-summary-block">
      <span className="mc-eyebrow">{label}</span>
      <span className="mc-summary-value">{value ?? '—'}</span>
    </div>
  );
}

function ListBlock({ label, items }: { label: string; items: string[] }): JSX.Element {
  return (
    <div className="mc-summary-block">
      <span className="mc-eyebrow">{label}</span>
      {items.length === 0 ? (
        <span className="mc-summary-value">—</span>
      ) : (
        <ul className="mc-summary-list">
          {items.map((item, i) => (
            <li key={`${i.toString()}:${item}`} className="mc-summary-value">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Formata bytes em rótulo humano enxuto (KB/MB). */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes.toString()} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb).toString()} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

const SENDER_LABEL: Record<string, string> = {
  customer: 'enviado pela pessoa',
  attendant: 'enviado pelo atendente',
  ai: 'enviado pela IA',
  system: 'do sistema',
};

function PeekMidias({ media }: { media: ContextPeekMediaItem[] }): JSX.Element {
  return (
    <div className="mc-media-list">
      {media.length === 0 ? (
        <span className="mc-mono-meta">Nenhuma mídia recebida nesta conversa.</span>
      ) : (
        media.map((item) => {
          const isImage = item.contentType.startsWith('image/');
          const senderLabel = SENDER_LABEL[item.sender] ?? item.sender;
          return (
            <div key={item.id} className="mc-media-item">
              <span className="mc-media-thumb" aria-hidden>
                {isImage ? (
                  <ImageIcon size={15} strokeWidth={1.5} />
                ) : (
                  <FileText size={15} strokeWidth={1.5} />
                )}
              </span>
              <span className="mc-media-meta">
                <b>{item.filename}</b>
                <span>
                  {senderLabel} · {formatBytes(item.sizeBytes)}
                </span>
              </span>
            </div>
          );
        })
      )}
      {media.length > 0 && (
        <div className="mc-media-note">Apenas mídias já recebidas na conversa. Sem novo upload aqui.</div>
      )}
    </div>
  );
}

const NOTE_VISIBILITY_LABEL: Record<string, string> = {
  public_team: 'time',
  private_author: 'só o autor',
  private_supervisors: 'supervisão',
};

function PeekNotas({ notes }: { notes: ContextPeekNoteItem[] }): JSX.Element {
  return (
    <div className="mc-notes">
      {notes.length === 0 ? (
        <span className="mc-mono-meta">Nenhuma nota nesta conversa.</span>
      ) : (
        notes.map((note) => {
          const author = note.authorName ?? 'Autor não identificado';
          const role = note.authorRole !== null ? ` (${note.authorRole})` : '';
          const visibility = NOTE_VISIBILITY_LABEL[note.visibility] ?? note.visibility;
          return (
            <div key={note.id} className="mc-note">
              <div className="mc-note-body">{note.body}</div>
              <div className="mc-mono-meta">
                {author}
                {role} · {visibility}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
